import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import type { Product } from '../lib/types'
import { Button } from './ui/Button'
import { WarnCard } from './ui/WarnCard'
import { ConfirmSummary } from './ui/ConfirmSummary'

export interface SummaryRow {
  label: string
  value: string
}

/** The purchase confirmation (MS-820; prototype `confirmPurchase`): the
 * warning states the commitment, the summary states what is being bought —
 * one row per fact, the total last and emphasised — and Confirm stays inert
 * until the authorisation box is ticked.
 *
 * Unlike the prototype's fire-and-forget modal, this one waits for the
 * server: Confirm, Cancel and the backdrop are inert while the request is in
 * flight (`pending`), and a refused purchase is reported inline (`error`)
 * with the modal kept open, so nothing fails silently and the reader can
 * retry or cancel. */
export function ConfirmPurchaseModal({
  product,
  rows,
  pending = false,
  error,
  onConfirm,
  onCancel,
}: Readonly<{
  product: Product
  rows: readonly SummaryRow[]
  pending?: boolean
  error?: string | null
  onConfirm: () => void
  onCancel: () => void
}>) {
  const { t } = useTranslation()
  const [checked, setChecked] = useState(false)

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[1000]">
      {/* The backdrop is a sibling of the dialog, not its parent, so a click
          inside the dialog never reaches it. It is a pointer convenience —
          keyboard and screen-reader users close with Cancel — hence hidden
          from both. While the purchase is in flight, neither Cancel nor the
          backdrop closes the modal: closing drops the requestId, and the next
          confirmation would then be a second purchase rather than a retry. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="confirm-backdrop"
        disabled={pending}
        className="absolute inset-0 bg-black/40 cursor-default"
        onClick={onCancel}
      />
      <dialog
        open
        aria-modal="true"
        aria-labelledby="confirm-purchase-title"
        className="relative m-0 p-0 border-0 text-inherit bg-[var(--bg-card)] rounded-xl shadow-[var(--shadow-lg)] w-[460px] max-w-[90vw] overflow-hidden"
      >
        <div className="px-6 pt-5 pb-4 border-b border-[var(--border-light)]">
          <h3 id="confirm-purchase-title" className="text-[16px] font-semibold text-[var(--text-primary)]">
            {t('confirmPurchase.title')}
          </h3>
        </div>
        <div className="px-6 py-5">
          <WarnCard>
            <Trans
              i18nKey={product === 'Mambu MCP' ? 'confirmPurchase.warningMcp' : 'confirmPurchase.warningAgt'}
              components={{ strong: <strong /> }}
            />
          </WarnCard>
          <ConfirmSummary rows={rows} total className="mt-3.5" />
          <label className="flex gap-2 items-start mt-3.5 text-[12.5px] text-[var(--text-secondary)] cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 accent-[var(--accent)]"
            />
            <span>{t('confirmPurchase.checkbox')}</span>
          </label>
          {error && (
            <div role="alert" className="mt-3 text-[12.5px] text-[var(--danger)]">
              {error}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[var(--border-light)] flex justify-end gap-2">
          <Button variant="ghost" disabled={pending} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" disabled={!checked || pending} onClick={onConfirm}>
            {t('confirmPurchase.confirm')}
          </Button>
        </div>
      </dialog>
    </div>
  )
}
